<template>
	<div class="w-full font-sans">
		<!-- ── Header ── -->
		<div class="flex items-center gap-4 mb-6">
			<div
				class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0">
				<Settings class="w-5 h-5 text-primary-400" />
			</div>
			<div>
				<h2 class="font-display text-[15px] font-bold text-dark-700 dark:text-white">
					Settings
				</h2>
				<p class="text-[12px] text-muted dark:text-muted-light mt-0.5">
					Manage your event and notification preferences
				</p>
			</div>
		</div>

		<!-- ── Settings cards grid ── -->
		<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
			<div v-for="(item, index) in settingsList" :key="index" @click="forClient && openModal(item.key)"
				class="flex items-center gap-3 p-4 rounded-xl border transition-all duration-200 group"
				:class="forAdmin
					? 'cursor-not-allowed opacity-50 border-primary-100 dark:border-dark-500 bg-primary-50/30 dark:bg-dark-600/30'
					: 'cursor-pointer border-primary-100 dark:border-dark-500 bg-primary-50/50 dark:bg-dark-600/50 hover:bg-primary-50 dark:hover:bg-dark-600 hover:border-primary-300 dark:hover:border-primary-400/40 hover:-translate-y-0.5 hover:shadow-card'">
				<div
					class="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-400 transition-colors duration-200">
					<component :is="item.icon"
						class="w-4 h-4 text-primary-600 dark:text-primary-400 group-hover:text-white transition-colors duration-200" />
				</div>
				<span class="font-display text-[13px] font-semibold text-dark-700 dark:text-white">
					{{ item.title }}
				</span>
				<svg class="w-4 h-4 text-muted dark:text-muted-light ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-200"
					fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
				</svg>
			</div>
		</div>

		<!-- ══════════════════════════════════════════
         NOTIFICATION MODAL
    ══════════════════════════════════════════ -->
		<transition name="modal">
			<div v-if="isModalOpen"
				class="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-4"
				@click.self="closeModal">
				<div
					class="bg-surface dark:bg-dark-700 rounded-card shadow-dropdown w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-primary-100 dark:border-dark-600">
					<!-- Modal header -->
					<div class="flex items-center justify-between p-6 border-b border-primary-100 dark:border-dark-600">
						<div class="flex items-center gap-3">
							<div
								class="w-9 h-9 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center">
								<Bell class="w-4 h-4 text-primary-400" />
							</div>
							<div>
								<p
									class="text-[10px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-0.5">
									Settings
								</p>
								<h3 class="font-display text-[15px] font-bold text-dark-700 dark:text-white">
									{{settingsList.find((i) => i.key === activeSection)?.title}}
								</h3>
							</div>
						</div>
						<div class="flex items-center gap-2">
							<button @click="toggleView"
								class="px-3 py-1.5 rounded-xl text-[12px] font-semibold transition-colors duration-200 bg-primary-50 dark:bg-dark-600 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-dark-500 border border-primary-100 dark:border-dark-500">
								{{ showTable ? "← Back to Form" : "View Notifications" }}
							</button>
							<button @click="closeModal"
								class="w-8 h-8 rounded-xl bg-primary-50 dark:bg-dark-600 flex items-center justify-center text-muted dark:text-muted-light hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors duration-200">
								<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
										d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>
					</div>

					<div class="p-6">
						<!-- ── Table view ── -->
						<div v-if="showTable"
							class="overflow-x-auto rounded-xl border border-primary-100 dark:border-dark-600">
							<table class="w-full border-separate border-spacing-0">
								<thead>
									<tr>
										<th v-for="h in [
											'Subject',
											'Message',
											'Frequency',
											'Active',
											'Actions',
										]" :key="h" class="text-left text-[11px] font-bold uppercase tracking-widest px-4 py-3 text-muted dark:text-muted-light bg-primary-50 dark:bg-dark-600 border-b border-primary-100 dark:border-dark-500">
											{{ h }}
										</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(item, index) in settingsData" :key="index"
										class="hover:bg-primary-50 dark:hover:bg-dark-600/50 transition-colors">
										<td
											class="px-4 py-3 text-[13px] text-dark-700 dark:text-white/90 border-b border-primary-50 dark:border-dark-600">
											{{ item.Subject }}
										</td>
										<td
											class="px-4 py-3 text-[13px] text-muted dark:text-muted-light border-b border-primary-50 dark:border-dark-600 max-w-[180px] truncate">
											{{ item.Message }}
										</td>
										<td
											class="px-4 py-3 text-[13px] text-muted dark:text-muted-light capitalize border-b border-primary-50 dark:border-dark-600">
											{{ item.Recurrence }}
										</td>
										<td class="px-4 py-3 border-b border-primary-50 dark:border-dark-600">
											<span
												class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
												:class="item.Status
													? 'bg-primary-50 dark:bg-primary-400/15 text-primary-600 dark:text-primary-300'
													: 'bg-dark-100 dark:bg-dark-600 text-muted dark:text-muted-light'
													">
												<span class="w-1.5 h-1.5 rounded-full" :class="item.Status ? 'bg-primary-400' : 'bg-muted'
													" />
												{{ item.Status ? "Active" : "Disabled" }}
											</span>
										</td>
										<td class="px-4 py-3 border-b border-primary-50 dark:border-dark-600">
											<button v-if="item.Status" @click="disableRiminder(item.ID)"
												class="text-[12px] font-semibold text-red-400 hover:text-red-600 transition-colors duration-150">
												Disable
											</button>
											<span v-else
												class="text-[12px] text-muted dark:text-muted-light">Disabled</span>
										</td>
									</tr>
									<tr v-if="!settingsData.length">
										<td colspan="5"
											class="px-4 py-8 text-center text-[13px] text-muted dark:text-muted-light">
											No notifications set up yet.
										</td>
									</tr>
								</tbody>
							</table>
						</div>

						<!-- ── Form view ── -->
						<div v-else class="space-y-5">
							<!-- Frequency -->
							<div>
								<label class="modal-label">Frequency</label>
								<select v-model="frequency" class="modal-input">
									<option>Instant</option>
									<option>Once</option>
									<option>Daily</option>
									<option>Weekly</option>
									<option>Monthly</option>
								</select>
							</div>

							<!-- Subject -->
							<div>
								<label class="modal-label">Subject</label>
								<input type="text" v-model="subject" placeholder="Enter notification subject"
									class="modal-input" />
							</div>

							<!-- Message -->
							<div>
								<label class="modal-label">Message</label>
								<textarea v-model="message" placeholder="Enter notification message" maxlength="500"
									rows="4" class="modal-input resize-none" />
								<div class="flex justify-end mt-1">
									<span class="text-[11px]" :class="message.length > 450
										? 'text-red-400'
										: 'text-muted dark:text-muted-light'
										">
										{{ message.length }}/500
									</span>
								</div>
							</div>

							<!-- Date (Once) -->
							<div v-if="frequency === 'Once'">
								<label class="modal-label">Date</label>
								<input type="date" v-model="date" class="modal-input" />
							</div>

							<!-- Time(s) -->
							<div v-if="frequency !== 'Instant'">
								<label class="modal-label">Time(s)</label>

								<!-- Single time -->
								<div v-if="frequency === 'Once'">
									<input type="time" v-model="time" class="modal-input w-auto" />
								</div>

								<!-- Multiple times -->
								<div v-else class="flex flex-col gap-2">
									<div v-for="(t, idx) in times" :key="idx" class="flex items-center gap-3">
										<input type="time" v-model="times[idx]"
											class="modal-input w-auto flex-shrink-0" />
										<button type="button" @click="removeTime(idx)"
											class="text-[12px] font-semibold text-red-400 hover:text-red-600 transition-colors">
											Remove
										</button>
									</div>
									<button type="button" @click="addTime"
										class="self-start flex items-center gap-2 px-4 py-2 rounded-xl text-[12px] font-semibold border-2 border-dashed border-primary-300 dark:border-primary-400/40 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-400/10 transition-all duration-200">
										<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
												d="M12 4v16m8-8H4" />
										</svg>
										Add Time
									</button>
								</div>
							</div>

							<!-- Weekly days -->
							<div v-if="frequency === 'Weekly'">
								<label class="modal-label">Days of the Week</label>
								<div class="flex flex-wrap gap-2">
									<label v-for="day in [
										{ label: 'Mon', value: 'Monday' },
										{ label: 'Tue', value: 'Tuesday' },
										{ label: 'Wed', value: 'Wednesday' },
										{ label: 'Thu', value: 'Thursday' },
										{ label: 'Fri', value: 'Friday' },
										{ label: 'Sat', value: 'saturday' },
										{ label: 'Sun', value: 'Sunday' },
									]" :key="day.value" class="relative cursor-pointer">
										<input type="checkbox" :value="day.value" v-model="weekDays"
											class="sr-only peer" />
										<span
											class="inline-flex items-center justify-center w-12 h-9 rounded-lg text-[12px] font-semibold border border-primary-100 dark:border-dark-500 text-muted dark:text-muted-light peer-checked:bg-primary-400 peer-checked:text-white peer-checked:border-primary-400 hover:border-primary-300 dark:hover:border-primary-400/40 transition-all duration-150 cursor-pointer">
											{{ day.label }}
										</span>
									</label>
								</div>
							</div>

							<!-- Monthly days -->
							<div v-if="frequency === 'Monthly'">
								<label class="modal-label">Days of Month</label>
								<div class="grid grid-cols-7 gap-1.5">
									<label v-for="day in 31" :key="day" class="relative cursor-pointer">
										<input type="checkbox" :value="day" v-model="monthDays" class="sr-only peer" />
										<span
											class="flex items-center justify-center w-full aspect-square rounded-lg text-[12px] font-semibold border border-primary-100 dark:border-dark-500 text-muted dark:text-muted-light peer-checked:bg-primary-400 peer-checked:text-white peer-checked:border-primary-400 hover:border-primary-300 transition-all duration-150 cursor-pointer">
											{{ day }}
										</span>
									</label>
								</div>
							</div>

							<!-- Actions -->
							<div
								class="flex items-center justify-between gap-3 pt-2 border-t border-primary-100 dark:border-dark-600">
								<button @click="openMethodModal" :disabled="!subject || !message"
									class="flex-1 font-display font-bold text-[13px] py-3 rounded-xl bg-dark-700 dark:bg-primary-400 text-white hover:bg-primary-600 dark:hover:bg-primary-600 hover:-translate-y-0.5 shadow-card hover:shadow-teal disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transition-all duration-200">
									Save Settings
								</button>
								<button @click="resetForm"
									class="px-6 font-display font-bold text-[13px] py-3 rounded-xl bg-primary-50 dark:bg-dark-600 border border-primary-100 dark:border-dark-500 text-dark-400 dark:text-white/70 hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors duration-200">
									Reset
								</button>
							</div>
							<!-- Select method modal -->
							<!-- ===== Confirm Send-Method Modal ===== -->
							<Teleport to="body">
								<transition name="fade">
									<div v-if="selectMethod"
										class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
										@click.self="closeMethodModal">
										<div
											class="w-full max-w-sm rounded-2xl bg-white dark:bg-dark-700 shadow-2xl p-6 space-y-5 animate-modal-in">
											<div class="flex items-start justify-between">
												<div>
													<h3 class="font-display font-bold text-[16px] text-dark-700 dark:text-white">
														Where should we send this?
													</h3>
													<p class="text-[12px] text-muted dark:text-muted-light mt-1">
														Choose how you'd like to receive this reminder.
													</p>
												</div>
												<button @click="closeMethodModal"
													class="text-muted dark:text-muted-light hover:text-dark-700 dark:hover:text-white transition-colors">
													<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
															d="M6 18L18 6M6 6l12 12" />
													</svg>
												</button>
											</div>

											<div class="flex gap-2">
												<!-- Email -->
												<label
													class="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-[13px] font-medium transition-colors"
													:class="method === 'mail'
															? 'border-[#1a9b8a] bg-[#f0faf8] text-[#1a9b8a]'
															: 'border-[#e5eeec] text-[#6b8f89] hover:border-[#b8d9d3]'
														">
													<input v-model="method" type="radio" value="mail" class="sr-only" />
													<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
														fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
														stroke-linejoin="round" aria-hidden="true">
														<path d="M3 7l9 6 9-6" />
														<rect x="3" y="5" width="18" height="14" rx="2" />
													</svg>
													Email
												</label>

												<!-- WhatsApp -->
												<label
													class="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-[13px] font-medium transition-colors"
													:class="method === 'phone'
															? 'border-[#1a9b8a] bg-[#f0faf8] text-[#1a9b8a]'
															: 'border-[#e5eeec] text-[#6b8f89] hover:border-[#b8d9d3]'
														">
													<input v-model="method" type="radio" value="phone" class="sr-only" />
													<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
														fill="currentColor" aria-hidden="true">
														<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
														<path d="M12.004 2c-5.523 0-10 4.477-10 10 0 1.766.457 3.428 1.257 4.87L2 22l5.263-1.24A9.955 9.955 0 0012.004 22c5.523 0 10-4.477 10-10s-4.477-10-10-10zm0 18.2a8.19 8.19 0 01-4.174-1.14l-.3-.178-3.116.735.74-3.037-.196-.31A8.19 8.19 0 013.8 12.004c0-4.526 3.678-8.204 8.204-8.204 4.525 0 8.203 3.678 8.203 8.204 0 4.525-3.678 8.196-8.203 8.196z"/>
													</svg>

													WhatsApp
												</label>
												<!-- Both -->
												<label
													class="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-[13px] font-medium transition-colors"
													:class="method === 'all'
															? 'border-[#1a9b8a] bg-[#f0faf8] text-[#1a9b8a]'
															: 'border-[#e5eeec] text-[#6b8f89] hover:border-[#b8d9d3]'
														">
													<input v-model="method" type="radio" value="all" class="sr-only" />
													Both
												</label>
											</div>

											<div class="flex gap-3 pt-1">
												<button @click="saveSettings" :disabled="!method"
													class="flex-1 font-display font-bold text-[13px] py-3 rounded-xl bg-dark-700 dark:bg-primary-400 text-white hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
													Confirm & Save
												</button>
												<button @click="closeMethodModal"
													class="px-5 font-display font-bold text-[13px] py-3 rounded-xl bg-primary-50 dark:bg-dark-600 border border-primary-100 dark:border-dark-500 text-dark-400 dark:text-white/70 hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors duration-200">
													Cancel
												</button>
											</div>
										</div>
									</div>
								</transition>
							</Teleport>
						</div>
					</div>
				</div>
			</div>
		</transition>

		<!-- ══════════════════════════════════════════
         PREFERENCES MODAL
    ══════════════════════════════════════════ -->
		<transition name="modal">
			<div v-if="isPreferencesModalOpen"
				class="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-4"
				@click.self="closePreferencesModal">
				<div
					class="bg-surface dark:bg-dark-700 rounded-card shadow-dropdown w-full max-w-md border border-primary-100 dark:border-dark-600">
					<!-- Header -->
					<div class="flex items-center justify-between p-6 border-b border-primary-100 dark:border-dark-600">
						<div class="flex items-center gap-3">
							<div
								class="w-9 h-9 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center">
								<Settings class="w-4 h-4 text-primary-400" />
							</div>
							<div>
								<p
									class="text-[10px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-0.5">
									Settings
								</p>
								<h3 class="font-display text-[15px] font-bold text-dark-700 dark:text-white">
									Preferences
								</h3>
							</div>
						</div>
						<button @click="closePreferencesModal"
							class="w-8 h-8 rounded-xl bg-primary-50 dark:bg-dark-600 flex items-center justify-center text-muted dark:text-muted-light hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors duration-200">
							<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
									d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</div>

					<!-- Toggles -->
					<div class="p-6 space-y-2">
						<!-- Booking toggle -->
						<div
							class="flex items-center justify-between p-4 rounded-xl bg-primary-50/60 dark:bg-dark-600/60 border border-primary-100 dark:border-dark-500">
							<div>
								<p class="text-[13px] font-semibold text-dark-700 dark:text-white">
									Event Booking
								</p>
								<p class="text-[11px] text-muted dark:text-muted-light mt-0.5">
									{{ bookable ? "Booking is open" : "Booking is closed" }}
								</p>
							</div>
							<label class="relative inline-flex items-center cursor-pointer flex-shrink-0">
								<input type="checkbox" v-model="bookable" class="sr-only peer"
									@change="onToggleBooking" />
								<div
									class="w-11 h-6 rounded-full transition-colors duration-300 bg-dark-200 dark:bg-dark-500 peer-checked:bg-primary-400 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform after:duration-300 peer-checked:after:translate-x-5" />
							</label>
						</div>

						<!-- Music toggle -->
						<div
							class="flex items-center justify-between p-4 rounded-xl bg-primary-50/60 dark:bg-dark-600/60 border border-primary-100 dark:border-dark-500">
							<div>
								<p class="text-[13px] font-semibold text-dark-700 dark:text-white">
									Music Recommendations
								</p>
								<p class="text-[11px] text-muted dark:text-muted-light mt-0.5">
									{{
										enableMusicRecommandation
											? "Recommendations enabled"
											: "Recommendations disabled"
									}}
								</p>
							</div>
							<label class="relative inline-flex items-center cursor-pointer flex-shrink-0">
								<input type="checkbox" v-model="enableMusicRecommandation" class="sr-only peer"
									@change="onToggleMusicRecommandation" />
								<div
									class="w-11 h-6 rounded-full transition-colors duration-300 bg-dark-200 dark:bg-dark-500 peer-checked:bg-primary-400 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform after:duration-300 peer-checked:after:translate-x-5" />
							</label>
						</div>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import { Bell, Settings } from "lucide-vue-next";
import { useStore } from 'vuex';
import { computed, ref, watch } from "vue";
import { apiDataService } from "@/services/apiDataService";
import { showToast } from "@/constants/toast";

export default {
	name: "SttingsPage",
	components: { Bell, Settings },

	props: {
		eventsId: { type: Number, required: true },
		bookingOpen: { type: Boolean, required: false },
		musicRecommandation: { type: Boolean, required: false },
	},

	emits: ["update:bookingOpen", "update:musicRecommandation"],

	setup(props, { emit }) {
		const isModalOpen = ref(false);
		const bookable = ref(props.bookingOpen);
		const enableMusicRecommandation = ref(props.musicRecommandation);
		const isPreferencesModalOpen = ref(false);
		const activeSection = ref(null);
		// User Role
		const store = useStore();

		const userInfo = computed(() => store.state.auth.user || {});

		const forAdmin = computed(() => Number(userInfo.value.user_type) === 0);

		const forClient = computed(() => Number(userInfo.value.user_type) === 2);

		watch(
			() => props.bookingOpen,
			(val) => {
				bookable.value = val;
			},
		);


		const onToggleBooking = (event) => {
			const value = event.target.checked;
			bookable.value = value;
			emit("update:bookingOpen", value);
		};
		const onToggleMusicRecommandation = (event) => {
			const value = event.target.checked;
			enableMusicRecommandation.value = value;
			emit("update:musicRecommandation", value);
		};

		const settingsList = [
			{ key: "notification", title: "Notification", icon: Bell },
			{ key: "preferences", title: "Preferences", icon: Settings },
		];

		const frequency = ref("Instant");
		const subject = ref("");
		const message = ref("");
		const calendar = ref("Google Calendar");
		const weekDays = ref([]);
		const monthDays = ref([1]);
		const showTable = ref(false);
		const settingsData = ref([]);
		const selectMethod = ref(false);
		const method = ref("mail");
		const date = ref("");
		const time = ref("09:00");
		const times = ref(["09:00"]);

		const addTime = () => {
			times.value.push("09:00");
		};
		const removeTime = (index) => {
			times.value.splice(index, 1);
		};

		const openModal = (key) => {
			if (key === "preferences") {
				isPreferencesModalOpen.value = true;
				return;
			}
			activeSection.value = key;
			isModalOpen.value = true;
		};
		const closeModal = () => {
			isModalOpen.value = false;
			activeSection.value = null;
		};
		const closePreferencesModal = () => {
			isPreferencesModalOpen.value = false;
		};
		const resetForm = () => {
			frequency.value = "Instant";
			subject.value = "";
			message.value = "";
			calendar.value = "Google Calendar";
			times.value = ["09:00"];
		};

		const toggleView = () => {
			showTable.value = !showTable.value;
			if (showTable.value) fetchSettings();
		};

		const openMethodModal = () => {
			selectMethod.value = true;
		};

		const closeMethodModal = () => {
			selectMethod.value = false;
		};
		const saveSettings = async () => {
			const body = {
				subject: subject.value,
				message: message.value,
				method: method.value,
				recurrence: frequency.value.toLowerCase(),
			};
			if (frequency.value === "Once") {
				const dt = new Date(`${date.value}T${time.value}:00`);
				body.send_at = dt.toISOString();
			}
			if (frequency.value === "Daily") body.schedule_times = [...times.value];
			if (frequency.value === "Weekly") {
				body.day_of_week = [...weekDays.value];
				body.schedule_times = [...times.value];
			}
			if (frequency.value === "Monthly") {
				body.day_of_month = [...monthDays.value];
				body.schedule_times = [...times.value];
			}
			try {
				const response = await apiDataService.setReminder(props.eventsId, body);
				showToast("success", ` ${response?.data}`);
				closeModal();
				closeMethodModal();
				resetForm();
			} catch (error) {
				console.error("Error:", error);
			}
		};

		const fetchSettings = async () => {
			try {
				const response = await apiDataService.getRiminders(props.eventsId);
				settingsData.value = response?.data || [];
			} catch (error) {
				console.error("Error fetching settings:", error);
			}
		};


		const disableRiminder = async (id) => {
			try {
				const response = await apiDataService.disableReminder(
					props.eventsId,
					id,
				);
				showToast("success", ` ${response?.data}`);
				fetchSettings();
			} catch (error) {
				console.error("Error disabling reminder:", error);
			}
		};

		return {
			Bell,
			Settings,
			isModalOpen,
			activeSection,
			settingsList,
			frequency,
			subject,
			message,
			calendar,
			showTable,
			addTime,
			removeTime,
			saveSettings,
			toggleView,
			openModal,
			closeModal,
			resetForm,
			times,
			date,
			time,
			settingsData,
			isPreferencesModalOpen,
			closePreferencesModal,
			bookable,
			onToggleBooking,
			enableMusicRecommandation,
			onToggleMusicRecommandation,
			weekDays,
			monthDays,
			disableRiminder,
			forAdmin,
			forClient,
			selectMethod,
			method,
			closeMethodModal,
			openMethodModal,
		};
	},
};
</script>

<style scoped>
.modal-label {
	@apply block text-[12px] font-bold uppercase tracking-widest mb-2 text-muted dark:text-muted-light;
}

.modal-input {
	@apply w-full px-3.5 py-2.5 rounded-xl text-[13px] font-medium bg-primary-50/60 dark:bg-dark-600 border border-primary-100 dark:border-dark-500 text-dark-700 dark:text-white placeholder-muted dark:placeholder-muted-light outline-none transition-all duration-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 dark:focus:border-primary-400;
}

.modal-enter-active,
.modal-leave-active {
	transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
	opacity: 0;
}
</style>
